import { DecompositionSectionComponentButton } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._sections.decomposition._components.component-button";
import { DecompositionTreeButton } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._sections.decomposition._components.tree-button";
import {
  DecompositionTreeSheet,
  type DecompositionTreeSheetHandle,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.panel-content.views.meaning._sections.decomposition._components.tree-sheet";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Surface, SurfaceContent } from "@-/dev.improve.haniverse.components.ui.surface";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { cn } from "@-/com.npmjs.cn";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useCallback, useRef } from "react";
import { View } from "@-/com.npmjs.react-native";
import { hasMinLength, isNonEmpty } from "@-/com.npmjs.ts-array-length";

export function MorphemeMnemonicsMeaningViewDecompositionSection({
  className,
}: {
  className?: string;
}) {
  const [components, tier] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.components,
    (c) => c.readonlySlice.data.tier,
  );
  const isSimple = !hasMinLength(components, 2);
  // More than three components don't fit legibly side by side, so they wrap onto two rows
  // (the extra one of an odd count goes on the top row).
  const isTwoRows = hasMinLength(components, 4);
  const columnCount = Math.ceil(components.length / 2);
  const treeSheetRef = useRef<DecompositionTreeSheetHandle>(null);
  const openTreeSheet = useCallback(() => {
    treeSheetRef.current?.open();
  }, []);

  // A header-less surface: the component strip is its whole body, closed by the tree button.
  return (
    <Surface className={className} testID="meaning-decomposition">
      <SurfaceContent className="flex-row items-stretch p-1" testID="meaning-decomposition-strip">
        <View className="min-w-0 flex-1 justify-center">
          {isSimple ? (
            <View className="justify-center p-1">
              <Text className="text-center text-[12px] font-bold tracking-widest text-muted-foreground/60 uppercase">
                {tier === null ? "Primitive" : "Simple Character"}
              </Text>
            </View>
          ) : isTwoRows ? (
            // Compact component cells wrap into two rows while retaining the strip's 40px height.
            <View className="h-10">
              {[components.slice(0, columnCount), components.slice(columnCount)].map(
                (rowComponents, rowIndex) => (
                  <View
                    className={cn(
                      "flex-1 flex-row items-center",
                      rowIndex > 0 && "border-t border-border/20",
                    )}
                    key={rowIndex}
                  >
                    {Array.from({ length: columnCount }, (_, columnIndex) => {
                      const partCard = rowComponents[columnIndex];
                      return (
                        <View
                          className={cn("flex-1", columnIndex > 0 && "border-l border-border/20")}
                          key={`${partCard?.graphemes?.[0]?.glyph ?? "empty"}-${columnIndex}`}
                        >
                          {partCard === undefined ? null : (
                            <DecompositionSectionComponentButton compact partCard={partCard} />
                          )}
                        </View>
                      );
                    })}
                  </View>
                ),
              )}
            </View>
          ) : (
            <View className="flex-row items-center">
              {isNonEmpty(components) ? (
                components.map((partCard, index) => (
                  <View
                    className={cn("flex-1", index > 0 && "border-l border-border/20")}
                    key={`${partCard.graphemes?.[0]?.glyph ?? "component"}-${index}`}
                  >
                    <DecompositionSectionComponentButton partCard={partCard} />
                  </View>
                ))
              ) : (
                <View className="flex-1 justify-center px-3 py-2">
                  <Text className="text-[12px] leading-5 italic" colorVariant="muted">
                    No decomposition available.
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>
        <View className="justify-center border-l border-border/20 pl-1">
          <DecompositionTreeButton onPress={openTreeSheet} />
        </View>
      </SurfaceContent>
      <DecompositionTreeSheet ref={treeSheetRef} />
    </Surface>
  );
}
